import { isAbsenceBrick } from '../../utils/absences';
import { uid } from '../../utils/format';
import { roundHoursToCents } from '../../utils/hours';
import {
  getClientByBrick,
  getPhaseById,
  isPhaseEntry,
  isProjectEntry,
  phaseBrickId,
  phaseIdFromEntry,
  projectIdFromEntry,
} from '../../utils/phases';

// Dérive client/phase depuis le brique_id existant (nouveau format __phase_, legacy __proj_
// encore lisible s'il n'a pas été migré, ou brique réelle).
function projectAndPhaseOf(clients, bid) {
  if (isPhaseEntry(bid)) {
    const res = getPhaseById(clients, phaseIdFromEntry(bid));
    return res ? { projetId: res.client.id, phaseId: res.phase.id } : { projetId: '', phaseId: '' };
  }
  if (isProjectEntry(bid)) return { projetId: projectIdFromEntry(bid), phaseId: '' };
  const res = getClientByBrick(clients, bid);
  return res ? { projetId: res.client.id, phaseId: res.phase.id } : { projetId: '', phaseId: '' };
}

// Lignes en lecture seule dans la saisie : absences (gérées dans Mon profil / Équipe) et temps
// importé de Jira (se corrige dans Jira ; il s'ajoute à la saisie manuelle).
export const isLockedEntry = (e) => isAbsenceBrick(e.brique_id) || e.source === 'jira';

/* Brouillon de saisie d'une journée : absences et temps Jira en lecture seule d'abord, puis les
   heures modifiables (ou une ligne vide). */
export function dayDraftFromEntries(entries, clients) {
  const locked = entries.filter(isLockedEntry);
  const editable = entries.filter((e) => !isLockedEntry(e));
  const ligne = (e) => ({ heures: String(e.heures), note: e.note || '' });
  return [
    ...locked.map((e) => ({
      key: uid(),
      ...projectAndPhaseOf(clients, e.brique_id),
      briqueId: e.brique_id,
      ...ligne(e),
      source: e.source || 'manuel',
      locked: true,
    })),
    ...(editable.length > 0
      ? editable.map((e) => {
          const { projetId, phaseId } = projectAndPhaseOf(clients, e.brique_id);
          const generale = isProjectEntry(e.brique_id) || isPhaseEntry(e.brique_id);
          return {
            key: uid(),
            projetId,
            phaseId,
            briqueId: generale ? '' : e.brique_id,
            ...ligne(e),
          };
        })
      : [{ key: uid(), projetId: '', phaseId: '', briqueId: '', heures: '', note: '' }]),
  ];
}

/* Lignes avec des heures et un projet mais sans phase (ni brique) : dayRows les ignorerait, et
   l'enregistrement effacerait alors la saisie sans rien dire. */
export function rowsMissingPhase(draft) {
  return draft.filter(
    (r) =>
      !r.locked &&
      r.projetId &&
      parseFloat(r.heures) > 0 &&
      !r.phaseId &&
      !(r.briqueId && !isAbsenceBrick(r.briqueId)),
  );
}

/* Lignes à enregistrer pour une journée, agrégées par brique AVANT l'écriture : deux lignes de la
   saisie tombant sur la même brique (ex. deux entrées "projet global" sans brique précise)
   violeraient sinon la contrainte unique (user_id, date, brique_id) — l'upsert groupé échouerait
   entièrement. */
export function dayRows(draft, { userId, devId, date }) {
  const rows = new Map();
  for (const r of draft) {
    if (r.locked || !r.projetId || !(parseFloat(r.heures) > 0)) continue;
    // Si brique sélectionnée → utilise son ID ; sinon → entrée "général" de la phase choisie.
    let brique_id = null;
    if (r.briqueId && !isAbsenceBrick(r.briqueId)) brique_id = r.briqueId;
    else if (r.phaseId) brique_id = phaseBrickId(r.phaseId);
    if (!brique_id) continue; // phase pas encore choisie (client à plusieurs phases) : ligne ignorée
    const heures = parseFloat(r.heures);
    const ex = rows.get(brique_id);
    if (ex) {
      ex.heures += heures;
      if (r.note && !ex.note) ex.note = r.note;
    } else
      rows.set(brique_id, {
        user_id: userId,
        dev_id: devId,
        date,
        brique_id,
        heures,
        note: r.note || null,
      });
  }
  return roundHoursToCents([...rows.values()]);
}
